import { useEffect, useState } from "react";
import { api, type RetraitCommande } from "@/lib/firebase/api-client";
import { handleError, handleSuccess } from "@/lib/errors/error-handler";

/**
 * ============================================================================
 *  LES RETRAITS D'ARTICLE QUI ATTENDENT LE PATRON (21/10/2026)
 * ============================================================================
 *
 *  Le responsable commande ne trouve pas un article : il demande qu'on le
 *  retire de la commande. C'est le patron qui decide — sa regle : « il
 *  m'envoie la demande et c'est a moi d'approuver ou non ».
 *
 *  Une commande attend derriere chaque ligne : elle ne peut pas partir
 *  tant que la demande n'est pas tranchee. D'ou un bandeau rouge en haut
 *  des commandes, relu toutes les 20 secondes.
 *
 *  Le support voit les demandes ; seul le patron a les boutons.
 *  Rien ne s'affiche quand rien n'attend.
 * ============================================================================
 */

const MAGASINS: Record<string, string> = {
  "2EqQqqb3Amrhm3h0P0tS": "Oulfa",
  e8Mn6vPL4euqB4Vt68Ym: "Bourgogne",
};

function depuis(iso: string | null): string {
  if (!iso) return "";
  const min = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return min < 1 ? "à l'instant" : `il y a ${min} min`;
}

export default function RetraitsCommande() {
  const [retraits, setRetraits] = useState<RetraitCommande[]>([]);
  const [peutDecider, setPeutDecider] = useState(false);
  const [aConfirmer, setAConfirmer] = useState<string | null>(null);
  const [enCours, setEnCours] = useState<string | null>(null);

  async function charger() {
    try {
      const r = await api.retraitsCommande.liste("EN_ATTENTE");
      setRetraits(r.retraits || []);
      setPeutDecider(r.peutDecider === true);
    } catch {
      /* un bandeau qui ne se charge pas ne doit pas casser la page */
    }
  }

  useEffect(() => {
    void charger();
    const minuteur = setInterval(() => void charger(), 20000);
    return () => clearInterval(minuteur);
  }, []);

  async function valider(r: RetraitCommande) {
    try {
      setEnCours(r.id);
      const res = await api.retraitsCommande.valider(r.id);
      if (res.statut === "RETIRE_GLOVO") {
        handleSuccess(`${r.article} : retiré chez Glovo.`);
      } else {
        handleSuccess(
          `${r.article} : retrait validé. Glovo n'a pas suivi — ${res.raison || "à retirer sur la tablette"}`,
        );
      }
      await charger();
    } catch (e: any) {
      handleError(e, "Valider le retrait");
    } finally {
      setEnCours(null);
      setAConfirmer(null);
    }
  }

  async function refuser(r: RetraitCommande) {
    try {
      setEnCours(r.id);
      await api.retraitsCommande.refuser(r.id);
      handleSuccess(`${r.article} : retrait refusé, l'article sera préparé.`);
      await charger();
    } catch (e: any) {
      handleError(e, "Refuser le retrait");
    } finally {
      setEnCours(null);
      setAConfirmer(null);
    }
  }

  if (retraits.length === 0) return null;

  return (
    <div className="rounded-xl border-2 border-red-400 bg-red-50 p-3 shadow-md">
      <p className="mb-2 flex items-center gap-2 text-sm font-black uppercase tracking-wide text-red-800">
        <span className="inline-block h-2.5 w-2.5 animate-pulse rounded-full bg-red-600" />
        {retraits.length === 1
          ? "1 retrait d'article attend ta décision"
          : `${retraits.length} retraits d'article attendent ta décision`}
      </p>
      <div className="space-y-2">
        {retraits.map((r) => (
          <div
            key={r.id}
            className="flex flex-col gap-2 rounded-lg border border-red-200 bg-white p-3 md:flex-row md:items-center md:justify-between"
          >
            <div className="min-w-0">
              <p className="text-sm font-bold text-gray-900">
                {r.quantite} × {r.article}
                <span className="ml-2 font-semibold text-red-700 tabular-nums">
                  {(r.montantCentimes / 100).toFixed(2).replace(/\.00$/, "")} DH
                </span>
              </p>
              <p className="text-xs text-gray-600">
                Commande {r.orderCode || r.orderId.slice(0, 8)}
                {r.storeId ? ` · ${MAGASINS[r.storeId] || r.storeId}` : ""}
                {" · "}demandé par {r.demandeParNom} {depuis(r.demandeLe)}
                {r.motif ? ` · « ${r.motif} »` : ""}
              </p>
              {!r.retraitApiPossible && (
                <p className="mt-0.5 text-xs font-semibold text-orange-700">
                  Retrait par l'API impossible pour cette commande : il faudra le faire sur la tablette.
                </p>
              )}
            </div>
            {peutDecider ? (
              <div className="flex shrink-0 gap-2">
                {aConfirmer === r.id ? (
                  <>
                    <button
                      type="button"
                      onClick={() => void valider(r)}
                      disabled={enCours === r.id}
                      className="rounded-lg bg-red-600 px-3 py-2 text-xs font-bold text-white disabled:opacity-50"
                    >
                      {enCours === r.id ? "..." : "Confirmer le retrait"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setAConfirmer(null)}
                      disabled={enCours === r.id}
                      className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-bold text-gray-700"
                    >
                      Annuler
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setAConfirmer(r.id)}
                      disabled={enCours === r.id}
                      className="rounded-lg bg-red-600 px-3 py-2 text-xs font-bold text-white disabled:opacity-50"
                    >
                      Valider le retrait
                    </button>
                    <button
                      type="button"
                      onClick={() => void refuser(r)}
                      disabled={enCours === r.id}
                      className="rounded-lg border-2 border-gray-300 bg-white px-3 py-2 text-xs font-bold text-gray-800 disabled:opacity-50"
                    >
                      Refuser — à préparer
                    </button>
                  </>
                )}
              </div>
            ) : (
              <p className="shrink-0 text-xs font-semibold text-gray-600">En attente du patron</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
